// Client-safe RBAC configuration for the admin panel.
// The source of truth for section access — imported by both the route
// guard/tab-filter (client) and reflected by the server middleware.

export const ADMIN_ROLES = ["admin", "ops", "whatsapp_staff"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ADMIN_SECTIONS = [
  "control_tower",
  "analytics",
  "insights",
  "intelligence",
  "acquisition_analytics",
  "conversion_sources",
  "leads",
  "orders",
  "shipping",
  "billing",
  "products",
  "inventory",
  "batches",
  "reviews",
  "support",
  "reservations",
  "webhooks",
  "emails",
  "messaging",
  "whatsapp",
  "reconcile",
  "affiliate",
  "loyalty",
  "coupons",
  "users",
  "audit",
  "hubspot",
  "ai_models",
  "payments",
  "content",
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

/**
 * Sections an `ops` user is allowed to see. `admin` sees everything.
 * Everything not listed here is admin-only (products, billing, reconcile,
 * user management, audit log, analytics/insights/leads).
 */
const OPS_SECTIONS: readonly AdminSection[] = [
  "control_tower",
  "orders",
  "shipping",
  "inventory",
  "reviews",
  "support",
  "webhooks",
  "emails",
  "messaging",
  "whatsapp",
] as const;

/**
 * `whatsapp_staff` is a single-purpose staff role: the WhatsApp inbox and
 * nothing else, so team members can answer customers without being given
 * orders, customers, payments or settings.
 */
const WHATSAPP_STAFF_SECTIONS: readonly AdminSection[] = ["whatsapp"] as const;

export function allowedSectionsFor(roles: readonly string[]): AdminSection[] {
  if (roles.includes("admin")) return [...ADMIN_SECTIONS];
  if (roles.includes("ops")) return [...OPS_SECTIONS];
  if (roles.includes("whatsapp_staff")) return [...WHATSAPP_STAFF_SECTIONS];
  return [];
}

export function canAccessSection(
  roles: readonly string[],
  section: AdminSection,
): boolean {
  return allowedSectionsFor(roles).includes(section);
}

export function firstAllowedSection(
  roles: readonly string[],
): AdminSection | null {
  return allowedSectionsFor(roles)[0] ?? null;
}

export const SECTION_LABEL: Record<AdminSection, string> = {
  control_tower: "Control Tower",
  analytics: "Analytics",
  insights: "Insights",
  intelligence: "Intelligence",
  acquisition_analytics: "Acquisition funnel",
  conversion_sources: "Conversion Sources",
  leads: "Leads",
  orders: "Orders",
  shipping: "Fulfilment",
  billing: "Billing",
  products: "Products",
  inventory: "Inventory & Prices",
  batches: "Batch Certificates",
  reviews: "Reviews",
  support: "Support & Refunds",
  reservations: "Pre-Reservations",
  webhooks: "Webhooks",
  emails: "Emails",
  messaging: "Messaging",
  whatsapp: "WhatsApp",
  reconcile: "Reconciliation",
  users: "Users & Roles",
  audit: "Audit log",
  affiliate: "Affiliate",
  loyalty: "TruCoins",
  coupons: "Coupons",
  hubspot: "HubSpot",
  ai_models: "AI models",
  payments: "Payment gateway",
  content: "Blog & content",
};

/**
 * Explicit capability matrix: which sections need a live 2FA session on top
 * of the section grant. This is the single source of truth — the server
 * middleware reads it, so a section can never be "sensitive in the docs but
 * unguarded in code". Money movement, access control and anything that can
 * silently change what customers are charged sit behind step-up auth.
 */
export const SECTION_REQUIRES_2FA: Record<AdminSection, boolean> = {
  control_tower: false,
  analytics: false,
  insights: false,
  intelligence: false,
  acquisition_analytics: false,
  conversion_sources: false,
  leads: false,
  orders: false,
  shipping: false,
  billing: true,
  products: false,
  inventory: false,
  batches: false,
  reviews: false,
  support: false,
  reservations: false,
  webhooks: false,
  emails: false,
  messaging: false,
  whatsapp: false,
  reconcile: true,
  affiliate: false,
  loyalty: false,
  coupons: false,
  users: true,
  audit: false,
  hubspot: false,
  ai_models: false,
  payments: true,
  content: false,
};

export function sectionRequires2fa(section: AdminSection): boolean {
  return SECTION_REQUIRES_2FA[section] === true;
}




/**
 * Sections surfaced inside the "Reports" hub. They stay individual
 * AdminSections for authz (DB grants are unchanged); the sidebar and the
 * /admin route present them as one "Reports" entry with internal tabs.
 */
export const REPORT_SECTIONS = ["analytics", "insights", "intelligence"] as const;
export type ReportSection = (typeof REPORT_SECTIONS)[number];

/**
 * Sidebar / route-level key: every real section, the "reports" hub alias and
 * "edit_website", which opens the Store Editor at its own route rather than a
 * section of /admin. Both aliases are derived from real grants — they are not
 * separate database sections.
 */
export type AdminNavKey = AdminSection | "reports" | "edit_website";

export function isReportSection(s: string): s is ReportSection {
  return (REPORT_SECTIONS as readonly string[]).includes(s);
}

export function canAccessReports(sections: readonly AdminSection[]): boolean {
  return REPORT_SECTIONS.some((s) => sections.includes(s));
}

/** The Store Editor rides on the same grant as the rest of content work. */
export function canEditWebsite(sections: readonly AdminSection[]): boolean {
  return sections.includes("content");
}

export const NAV_LABEL: Record<AdminNavKey, string> = {
  ...SECTION_LABEL,
  reports: "Reports",
  edit_website: "Edit website",
};

/**
 * Single source of truth for turning a `user_sections` RPC result into the
 * caller's effective sections. Fails CLOSED: a *successful* RPC that returns
 * nothing means "granted nothing" — a scoped user whose grants were revoked
 * must never fall back to the role-wide map. Only an actual RPC error falls
 * back, so a transient DB hiccup does not lock real admins out.
 */
export function resolveAccessSections(input: {
  roles: readonly string[];
  sections: readonly string[] | null | undefined;
  rpcErrored: boolean;
}): AdminSection[] {
  if (input.rpcErrored) return allowedSectionsFor(input.roles);
  const known = new Set<string>(ADMIN_SECTIONS);
  return (input.sections ?? []).filter((s): s is AdminSection => known.has(s));
}
